import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { Expose, Type } from "class-transformer";
import { IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class CentralStationConfiguration extends ProductConfiguration {
  @IsString()
  public familyNumber: string;

  @IsEnum(MonitoringCategory)
  @Expose({ name: "category" })
  private _category!: MonitoringCategory;

  @Type(() => MonitoringProductItem)
  @Expose({ name: "family" })
  private _family?: MonitoringProductItem;

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @ValidateNested({ each: true })
  @Expose({ name: "baseOptions" })
  private _baseOptions?: MonitoringProductItem[];

  @Type(() => MonitoringProductItem)
  @Expose({ name: "licence" })
  @ValidateNested()
  private _licence!: MonitoringProductItem;

  @Type(() => MonitoringProductItem)
  @ValidateNested({ each: true })
  @Expose({ name: "additionalLicences" })
  private _additionalLicences?: MonitoringProductItem[];

  @Type(() => MonitoringProductItem)
  @ValidateNested({ each: true })
  @Expose({ name: "accessories" })
  private _accessories?: MonitoringProductItem[];

  @IsOptional()
  @Expose()
  @Type(() => MonitoringProductItem)
  attachments?: MonitoringProductItem[];

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    family: MonitoringProductItem | undefined,
    licence: MonitoringProductItem,
    additionalLicences: MonitoringProductItem[],
    accessories: MonitoringProductItem[],
    attachments?: MonitoringProductItem[],
    baseOptions?: MonitoringProductItem[],
  ) {
    super(id, name, project);
    this.familyNumber = familyNumber;
    this.category = category;
    this.family = family;
    this.baseOptions = baseOptions;
    this.licence = licence;
    this.additionalLicences = additionalLicences;
    this.accessories = accessories;
    this.attachments = attachments;
  }

  static create(
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    family: MonitoringProductItem | undefined,
    licence: MonitoringProductItem,
    additionalLicences: MonitoringProductItem[],
    accessories: MonitoringProductItem[],
    attachments?: MonitoringProductItem[],
    baseOptions?: MonitoringProductItem[],
  ): CentralStationConfiguration {
    return new CentralStationConfiguration(
      uuidv4(),
      name,
      project,
      familyNumber,
      category,
      family,
      licence,
      additionalLicences,
      accessories,
      attachments,
      baseOptions,
    );
  }

  update(
    familyName: string,
    familyNumber: string,
    family: MonitoringProductItem | undefined,
    licence: MonitoringProductItem,
    additionalLicences: MonitoringProductItem[],
    accessories: MonitoringProductItem[],
    attachments?: MonitoringProductItem[],
    baseOptions?: MonitoringProductItem[],
  ) {
    this.name = familyName;
    this.familyNumber = familyNumber;
    this.family = family;
    this.baseOptions = baseOptions;
    this.licence = licence;
    this.additionalLicences = additionalLicences;
    this.accessories = accessories;
    this.attachments = attachments;
    this.project.publishUpdate(ProjectEventType.CENTRAL_STATION_CONFIGURATION_UPDATED, this);
  }

  getDeviceMatNr() {
    const matNr: string[] = [
      ...this.baseOptionsDevices,
      ...this._licence.option.devices,
      ...this.additionalLicencesDevices,
      ...this.accessoriesDevices,
    ];

    for (const attachment of this.attachments ?? []) {
      matNr.push(...attachment.option.devices);
    }
    return matNr;
  }

  override get mainProductId(): string {
    return this.familyNumber;
  }

  get category(): MonitoringCategory {
    return this._category;
  }

  get family(): MonitoringProductItem | undefined {
    return this._family;
  }

  get baseOptions(): MonitoringProductItem[] {
    return this._baseOptions?.length ? this._baseOptions : this._family ? [this._family] : [];
  }

  get licence(): MonitoringProductItem {
    return this._licence;
  }

  get additionalLicences(): MonitoringProductItem[] {
    return this._additionalLicences ?? [];
  }

  get accessories(): MonitoringProductItem[] {
    return this._accessories ?? [];
  }

  set category(value: MonitoringCategory) {
    this._category = value;
  }

  set family(value: MonitoringProductItem | undefined) {
    this._family = value;
  }

  set baseOptions(value: MonitoringProductItem[] | undefined) {
    this._baseOptions = value?.length ? value : undefined;
  }

  set licence(value: MonitoringProductItem) {
    this._licence = value;
  }

  set additionalLicences(value: MonitoringProductItem[]) {
    this._additionalLicences = value;
  }

  set accessories(value: MonitoringProductItem[]) {
    this._accessories = value;
  }

  get configuredProductIds(): string[] {
    return [
      ...this.baseOptionsDevices,
      ...this.accessoriesDevices,
      ...this.additionalLicencesDevices,
      ...this.licence.option.devices,
      ...this.attachmentDevices,
    ];
  }

  getOptions(): MonitoringProductItem[] {
    return [...this.baseOptions, this.licence, ...this.additionalLicences, ...this.accessories];
  }

  getAllItems(): MonitoringProductItem[] {
    return [...this.getOptions(), ...(this.attachments ?? [])];
  }

  get additionalLicencesDevices(): string[] {
    return this.additionalLicences.flatMap((attachment) => attachment.option.devices);
  }

  get accessoriesDevices(): string[] {
    return this.accessories.flatMap((attachment) => attachment.option.devices);
  }

  get baseOptionsDevices(): string[] {
    return this.baseOptions.flatMap((item) => item.option.devices);
  }

  get attachmentDevices(): string[] {
    return this.attachments?.flatMap((attachment) => attachment.option.devices) ?? [];
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedCentralStations(this) : 0;
  }
}
